import { Trans } from '@/components/Trans'
import { BILLING_METHODS, BillingMethod, DURATION, Duration } from '@/consts/order'
import { cn } from '@/utils/style'

interface RenewBillingMethodsProps {
  billingMethod: BillingMethod
  duration: Duration
  onBillingMethodChange: (value: BillingMethod) => void
  onDurationChange: (value: Duration) => void
}

export function RenewBillingMethods({
  billingMethod,
  duration,
  onBillingMethodChange,
  onDurationChange,
}: RenewBillingMethodsProps) {
  const payAsYouGoDisabled = true

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="t-sb text-secondary">
          <Trans i18nKey="create.billingMethods">Billing Method</Trans>
        </div>

        <div className="flex flex-row gap-3">
          <MethodCard
            selected={billingMethod === BILLING_METHODS.SUBSCRIPTION}
            onClick={() => onBillingMethodChange(BILLING_METHODS.SUBSCRIPTION)}
          >
            <Trans i18nKey="create.billingMethodsOptions.subscription">Subscription</Trans>
          </MethodCard>

          <MethodCard
            selected={billingMethod === BILLING_METHODS.PAY_AS_YOU_GO}
            disabled={payAsYouGoDisabled}
            onClick={() => {
              if (payAsYouGoDisabled) return
              onBillingMethodChange(BILLING_METHODS.PAY_AS_YOU_GO)
            }}
          >
            <Trans i18nKey="create.billingMethodsOptions.payAsYouGo">Pay-as-you-go</Trans>
          </MethodCard>
        </div>
      </div>

      {/* Duration（只有 Subscription 才顯示） */}
      <div className="flex flex-col gap-2">
        <div className="t-sb text-secondary">
          <Trans i18nKey="create.duration">Duration</Trans>
        </div>

        <div className="flex flex-row gap-3 w-full">
          <DurationCard
            selected={duration === DURATION.DAILY}
            onClick={() => onDurationChange(DURATION.DAILY)}
          >
            <Trans i18nKey="create.durationOptions.daily">Daily</Trans>
          </DurationCard>

          <DurationCard
            selected={duration === DURATION.MONTHLY}
            onClick={() => onDurationChange(DURATION.MONTHLY)}
          >
            <Trans i18nKey="create.durationOptions.monthly">Monthly</Trans>
          </DurationCard>
        </div>
      </div>
    </div>
  )
}

interface MethodCardProps {
  selected: boolean
  disabled?: boolean
  children: React.ReactNode
  onClick?: () => void
}

function MethodCard({ selected, disabled, children, onClick }: MethodCardProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'w-full h-11 px-4 rounded-xl border flex items-center gap-3 text-left',
        'transition-colors',
        disabled && 'cursor-not-allowed opacity-50',
        !disabled &&
          (selected ? 'border-secondary' : 'border-neutral-200 bg-panel-white hover:bg-neutral-50'),
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center w-4 h-4 rounded-full border',
          selected ? 'border-secondary bg-btn-secondary' : 'border-neutral-300 bg-panel-white',
        )}
      >
        {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
      </span>

      <span className={cn('t-sb', selected ? 'text-primary' : 'text-primary')}>{children}</span>
    </button>
  )
}

interface DurationCardProps {
  selected: boolean
  disabled?: boolean
  children: React.ReactNode
  onClick?: () => void
}

function DurationCard({ selected, disabled, children, onClick }: DurationCardProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'w-full h-11 px-4 rounded-xl border flex items-center gap-3 text-left',
        'transition-colors',
        disabled && 'cursor-not-allowed opacity-50',
        !disabled &&
          (selected ? 'border-secondary' : 'border-neutral-200 bg-panel-white hover:bg-neutral-50'),
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center w-4 h-4 rounded-full border',
          selected ? 'border-secondary bg-btn-secondary' : 'border-neutral-300 bg-panel-white',
        )}
      >
        {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
      </span>

      <span className={cn('t-sb', selected ? 'text-primary' : 'text-primary')}>{children}</span>
    </button>
  )
}
